import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { AiOutlinePlus } from "react-icons/ai";
import { BsCheckCircleFill, BsExclamationTriangleFill, BsTrash } from "react-icons/bs";
import Label from "../../../../../../components/form_fields/Label";
import { BoolEnum } from "../../../../../../models/payment-setup.dto";
import {
	useGetAvailablePaymentDestinations,
	useGetPaymentProducts,
	useVerifyBankAccount,
} from "../../../../../../services/miscellaneous-payment.service";
import { FieldError, SectionHeading } from "../components";
import {
	PaymentDestination,
	PaymentSetupFormValues,
	emptyProduct,
	getBranchNames,
	inputClassName,
	toList,
	toRecord,
} from "../helpers";

type PaymentProduct = { code: string; name: string; description?: string };

interface ProductsStepProps {
	/** Products are optional when onboarding, but required when adding products later */
	requireAtLeastOne?: boolean;
	hideHeading?: boolean;
}

const ProductsStep = ({ requireAtLeastOne, hideHeading }: ProductsStepProps) => {
	const {
		register,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<PaymentSetupFormValues>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "products",
		rules: requireAtLeastOne
			? { validate: (products) => products.length > 0 || "Add at least one product" }
			: undefined,
	});

	const { data: productsResponse } = useGetPaymentProducts();
	const { data: destinationsResponse } = useGetAvailablePaymentDestinations();
	const products = toList<PaymentProduct>(productsResponse);
	const destinations = toList<PaymentDestination>(destinationsResponse);

	const { mutate: verifyBankAccount } = useVerifyBankAccount();
	const [verifyingIndex, setVerifyingIndex] = useState<number | null>(null);
	const [verifiedIndexes, setVerifiedIndexes] = useState<Record<string, boolean>>({});

	const formProducts = watch("products") || [];
	const companyName = watch("companyName");

	const verifyAccount = (index: number, fieldId: string) => {
		const details = formProducts[index]?.destinationDetails;
		const destination = destinations.find(
			(item) => item.destinationId === details?.destinationId,
		);
		if (!destination || !details?.destinationAccountNumber) return;

		setVerifyingIndex(index);
		verifyBankAccount(
			{
				payload: {
					bankCode: destination.code,
					destinationAccountNumber: details.destinationAccountNumber,
				},
			},
			{
				onSuccess: (response) => {
					const accountName = toRecord<{ accountName?: string }>(response)?.accountName;
					if (!accountName) return;
					setValue(`products.${index}.destinationDetails.destinationAccountName`, accountName, {
						shouldValidate: true,
					});
					setVerifiedIndexes((prev) => ({ ...prev, [fieldId]: true }));
				},
				onSettled: () => setVerifyingIndex(null),
			},
		);
	};

	return (
		<div>
			{!hideHeading && (
				<SectionHeading
					title="Products & settlement"
					description="Choose the payment products to enable and the account each one settles into. You can add products later."
				/>
			)}
			<div className="space-y-5">
				{fields.map((field, index) => {
					const product = formProducts[index] || emptyProduct;
					const details = product.destinationDetails;
					const productErrors = errors.products?.[index];
					const detailErrors = productErrors?.destinationDetails;
					const destination = destinations.find(
						(item) => item.destinationId === details.destinationId,
					);
					const branchNames = getBranchNames(destination);
					const accountNameMismatch =
						verifiedIndexes[field.id] &&
						!!companyName &&
						details.destinationAccountName.trim().toUpperCase() !==
							companyName.trim().toUpperCase();

					return (
						<div key={field.id} className="rounded-lg border border-gray-200 p-5">
							<div className="flex justify-between items-center mb-4">
								<h4 className="text-sm font-semibold text-gray-700">
									Product {index + 1}
								</h4>
								<button
									type="button"
									onClick={() => remove(index)}
									className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500"
								>
									<BsTrash /> Remove
								</button>
							</div>
							<div className="grid grid-cols-2 gap-x-6 gap-y-5">
								<div>
									<Label for={`products.${index}.code`} title="Product" />
									<select
										id={`products.${index}.code`}
										{...register(`products.${index}.code`, {
											required: "Select a product",
											onChange: (event) => {
												const selected = products.find(
													(item) => item.code === event.target.value,
												);
												if (!selected) return;
												setValue(`products.${index}.name`, selected.name);
												if (!product.description) {
													setValue(
														`products.${index}.description`,
														selected.description || "",
													);
												}
											},
										})}
										className={inputClassName(!!productErrors?.code)}
									>
										<option value="">Select product</option>
										{products.map((item) => (
											<option key={item.code} value={item.code}>
												{item.name}
											</option>
										))}
									</select>
									<FieldError error={productErrors?.code} />
								</div>
								<div>
									<Label for={`products.${index}.name`} title="Display name" />
									<input
										id={`products.${index}.name`}
										placeholder="Name for this company's instance"
										{...register(`products.${index}.name`, {
											required: "Enter a display name",
										})}
										className={inputClassName(!!productErrors?.name)}
									/>
									<FieldError error={productErrors?.name} />
								</div>
								<div className="col-span-2">
									<Label for={`products.${index}.description`} title="What it's used for" />
									<input
										id={`products.${index}.description`}
										placeholder="e.g. Salary payouts to employees"
										{...register(`products.${index}.description`, {
											required: "Describe what the product is used for",
											maxLength: { value: 200, message: "Use 200 characters or fewer" },
										})}
										className={inputClassName(!!productErrors?.description)}
									/>
									<FieldError error={productErrors?.description} />
								</div>

								<p className="col-span-2 text-xs font-semibold text-gray-600 -mb-2 mt-1">
									Settlement account
								</p>
								<div>
									<Label for={`products.${index}.destinationDetails.destinationId`} title="Bank / provider" />
									<select
										id={`products.${index}.destinationDetails.destinationId`}
										{...register(`products.${index}.destinationDetails.destinationId`, {
											required: "Select a bank or provider",
											onChange: (event) => {
												const selected = destinations.find(
													(item) => item.destinationId === event.target.value,
												);
												setValue(
													`products.${index}.destinationDetails.destinationTypeId`,
													selected?.destinationTypeId || "",
												);
												setValue(`products.${index}.destinationDetails.destinationBranchName`, "");
												setValue(`products.${index}.destinationDetails.destinationAccountName`, "");
												setVerifiedIndexes((prev) => ({ ...prev, [field.id]: false }));
											},
										})}
										className={inputClassName(!!detailErrors?.destinationId)}
									>
										<option value="">Select bank or provider</option>
										{destinations.map((item) => (
											<option key={item.destinationId} value={item.destinationId}>
												{item.name}
											</option>
										))}
									</select>
									<FieldError error={detailErrors?.destinationId} />
								</div>
								<div>
									<Label for={`products.${index}.destinationDetails.destinationBranchName`} title="Branch" />
									{branchNames.length ? (
										<select
											id={`products.${index}.destinationDetails.destinationBranchName`}
											{...register(`products.${index}.destinationDetails.destinationBranchName`, {
												required: "Select a branch",
											})}
											className={inputClassName(!!detailErrors?.destinationBranchName)}
										>
											<option value="">Select branch</option>
											{branchNames.map((branch) => (
												<option key={branch} value={branch}>
													{branch}
												</option>
											))}
										</select>
									) : (
										<input
											id={`products.${index}.destinationDetails.destinationBranchName`}
											placeholder="Enter branch name"
											{...register(`products.${index}.destinationDetails.destinationBranchName`, {
												required: "Enter the branch name",
											})}
											className={inputClassName(!!detailErrors?.destinationBranchName)}
										/>
									)}
									<FieldError error={detailErrors?.destinationBranchName} />
								</div>
								<div>
									<Label for={`products.${index}.destinationDetails.destinationAccountNumber`} title="Account number" />
									<div className="flex gap-2">
										<input
											id={`products.${index}.destinationDetails.destinationAccountNumber`}
											placeholder="Digits only"
											inputMode="numeric"
											{...register(`products.${index}.destinationDetails.destinationAccountNumber`, {
												required: "Enter the account number",
												pattern: {
													value: /^\d{6,34}$/,
													message: "Enter 6 to 34 digits, without spaces",
												},
												onChange: () =>
													setVerifiedIndexes((prev) => ({ ...prev, [field.id]: false })),
											})}
											className={inputClassName(!!detailErrors?.destinationAccountNumber)}
										/>
										<button
											type="button"
											onClick={() => verifyAccount(index, field.id)}
											disabled={
												!destination ||
												!/^\d{6,34}$/.test(details.destinationAccountNumber) ||
												verifyingIndex === index
											}
											className="mt-2 h-14 px-4 shrink-0 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
										>
											{verifyingIndex === index ? "Checking..." : "Verify"}
										</button>
									</div>
									<FieldError error={detailErrors?.destinationAccountNumber} />
								</div>
								<div>
									<Label for={`products.${index}.destinationDetails.destinationAccountName`} title="Account name" />
									<input
										id={`products.${index}.destinationDetails.destinationAccountName`}
										placeholder="Verify the account to fill this in"
										{...register(`products.${index}.destinationDetails.destinationAccountName`, {
											required: "Enter the account name",
										})}
										className={inputClassName(!!detailErrors?.destinationAccountName)}
									/>
									{verifiedIndexes[field.id] && !accountNameMismatch && (
										<p className="flex items-center gap-1 text-xs text-green-700 mt-1">
											<BsCheckCircleFill /> Account name confirmed by the bank
										</p>
									)}
									{accountNameMismatch && (
										<p className="flex items-start gap-1 text-xs text-amber-700 mt-1">
											<BsExclamationTriangleFill className="mt-0.5 shrink-0" />
											This doesn't match the registered company name. Settlement is often
											held when the names differ.
										</p>
									)}
									<FieldError error={detailErrors?.destinationAccountName} />
								</div>
								<label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={details.receiveStatement === BoolEnum.Y}
										onChange={(event) =>
											setValue(
												`products.${index}.destinationDetails.receiveStatement`,
												event.target.checked ? BoolEnum.Y : BoolEnum.N,
											)
										}
									/>
									Send account statements for this account
								</label>
							</div>
						</div>
					);
				})}

				<FieldError error={errors.products?.root} />

				<button
					type="button"
					onClick={() => append({ ...emptyProduct, destinationDetails: { ...emptyProduct.destinationDetails } })}
					className="flex items-center gap-1 text-sm text-primary-blue"
				>
					<AiOutlinePlus /> Add product
				</button>
			</div>
		</div>
	);
};

export default ProductsStep;
